import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import PropTypes from "prop-types";

import {
  AuthContext,
  JobsContext,
  MatchContext,
  ModalContext,
  NotesContext,
  SearchContext,
} from "context/providers";

import AppDialog from "components/AppDialog";
import NotesPanel from "components/NotesPanel";

const NoteModal = ({ user, modalTarget, title, onClose }) => {
  const { user: loggedInUser } = useContext(AuthContext);
  const { activeModal } = useContext(ModalContext);
  const { updateHitField } = useContext(SearchContext);
  const { updateMatchFieldLocally } = useContext(MatchContext);
  const { jobOpp } = useContext(JobsContext);
  const {
    userNotes,
    matchedNotes,
    user: initUserNotes,
    match: initMatchNotes,
    add: addNote,
    remove: removeNote,
  } = useContext(NotesContext);

  const [noteData, setNoteData] = useState({ isLoading: false });

  const { shouldInitMatchNotes, shouldInitUserNotes, match, hit } =
    useMemo(() => {
      const associatedApplication =
        modalTarget === "Match"
          ? { id: user?.applicationId }
          : (user?.applications || []).find(
              (app) => app.jobTypeId === jobOpp?.jobType?.id
            );

      const shouldInitMatchNotes =
        !matchedNotes[associatedApplication?.id] && associatedApplication;
      const shouldInitUserNotes = !userNotes[user.user?.id || user.id];

      const match = {
        ...user,
        id: associatedApplication?.id,
        applicationId: associatedApplication?.id,
      };

      const hit =
        modalTarget === "User" ? user : user.user || { id: activeModal.userId };

      return {
        shouldInitMatchNotes,
        shouldInitUserNotes,
        match,
        hit,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const initNotes = useCallback(
    async (initMatch, initUser) => {
      try {
        if (initMatch) {
          await initMatchNotes(match, jobOpp);
        }

        if (initUser) {
          await initUserNotes(hit);
        }
      } catch (error) {
        console.error("initUserNotes error: ", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const createUserNote = async (note) => {
    try {
      const { content, isPublic } = note;
      const { id: userId, notesCount } = hit;

      await addNote({
        userId,
        content,
        isPublic,
      });

      await initNotes(false, true);
      updateHitField(userId, "notesCount", (+notesCount || 0) + 1);

      return { isSuccess: true };
    } catch (error) {
      console.error("createNote error: ", error);

      return { isSuccess: false };
    }
  };

  const deleteUserNote = async (id) => {
    const { id: userId, notesCount } = hit;
    const newNotesCount = +notesCount > 0 ? +notesCount - 1 : +notesCount;

    await removeNote({ id });
    await initNotes(false, true);
    updateHitField(userId, "notesCount", newNotesCount);
  };

  const createMatchNote = async (note) => {
    try {
      const { content, isPublic } = note;
      const { id: jobOpportunityId } = jobOpp;
      const { applicationId, notesCount } = match;

      const params = {
        applicationId,
        jobOpportunityId,
        content,
        isPublic,
      };

      await addNote(params);

      await initNotes(true, false);

      updateMatchFieldLocally(applicationId, "notesCount", +notesCount + 1);

      return { isSuccess: true };
    } catch (error) {
      console.error("createNote error: ", error);

      return { isSuccess: false };
    }
  };

  const deleteMatchNote = async (id) => {
    const { applicationId, notesCount } = match;
    const newNotesCount = +notesCount > 0 ? +notesCount - 1 : +notesCount;

    await removeNote({ id });
    await initNotes(true, false);

    updateMatchFieldLocally(applicationId, "notesCount", newNotesCount);
  };

  const remove = (id, { target }) => {
    switch (target) {
      case "Match": {
        deleteMatchNote(id);
        break;
      }

      default: {
        deleteUserNote(id);
        break;
      }
    }
  };

  useEffect(() => {
    if (user) {
      (async () => {
        if (shouldInitMatchNotes || shouldInitUserNotes) {
          setNoteData((prev) => ({ ...prev, isLoading: true }));
          await initNotes(shouldInitMatchNotes, shouldInitUserNotes);
          setNoteData((prev) => ({ ...prev, isLoading: false }));
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const { notes, notesCount, label } = useMemo(() => {
    if (modalTarget === "User") {
      const notes = userNotes[hit.id] || [];

      return {
        label: "Notes",
        notes,
        notesCount: notes.length || hit.notesCount,
      };
    }

    const notes = matchedNotes[match.id] || [];

    return {
      label: "Match Notes",
      notes,
      notesCount: notes.length || match.notesCount,
    };
  }, [
    hit.id,
    hit.notesCount,
    match.id,
    match.notesCount,
    matchedNotes,
    modalTarget,
    userNotes,
  ]);

  return (
    <AppDialog
      open
      onClose={onClose}
      header={title}
      className="max-h-[65vh] w-[95%] max-w-none overflow-x-hidden rounded-lg md:w-10/12 xl:w-7/12"
      contentClassName="px-8 pb-8"
    >
      <div className="mt-6">
        {noteData.isLoading ? (
          <div className="loader !w-6 !h-6 mb-4" />
        ) : (
          <NotesPanel
            notes={notes}
            notesCount={notesCount}
            label={label}
            loggedInUser={loggedInUser}
            onSubmit={modalTarget === "User" ? createUserNote : createMatchNote}
            onDelete={(id) => remove(id, { target: modalTarget })}
          />
        )}
      </div>
    </AppDialog>
  );
};

NoteModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default NoteModal;
